import { generateId } from "@fleet/utils";
/* oxlint-disable lingui/no-unlocalized-strings -- TODO(i18n): translate (debt) */
import { createSignal, For, Show } from "solid-js";
import { Plus, Tags, Trash2 } from "@fleet/icons";
import { GlassSurface } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createPowerSyncQuery } from "@time-tracker/shared/solid";
import { useWorkspace } from "@/features/chronos-sync/workspace-context";
import { ensureWorkspace } from "@/features/chronos-sync/workspace";
import { and, eq, tag, type Tag } from "@time-tracker/powersync";
import { cn, liveTagFilters, patchRowVerified, queryKeys } from "@time-tracker/shared";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";

const TAG_COLOR_OPTIONS = [
  { value: "#afc6ff", label: "Electric Blue" },
  { value: "#ebb2ff", label: "Vivid Violet" },
  { value: "#ceffdf", label: "Neon Mint" },
  { value: "#f5a623", label: "Gold" },
  { value: "#ffb4ab", label: "Coral" },
] as const;

const tagName = (row: Tag) => row.name ?? "";

/**
 * Manager for tags in the active workspace. Tags are associated with
 * time records from the timeline edit dialog.
 */
export function WorkspaceTagsManager() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const { activeWorkspaceId } = useWorkspace();
  const { t } = useI18n();

  const [newTagName, setNewTagName] = createSignal("");
  const [newTagColor, setNewTagColor] = createSignal<string>(TAG_COLOR_OPTIONS[0].value);
  const [isAdding, setIsAdding] = createSignal(false);

  const tagsQuery = createPowerSyncQuery<Tag>({
    queryKey: () => queryKeys.chronos.workspaceTags(activeWorkspaceId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(tag)
        .where(and(eq(tag.workspace_id, activeWorkspaceId()), ...liveTagFilters()));
    },
    enabled: () => Boolean(drizzle()) && Boolean(activeWorkspaceId()),
    suspense: false,
  });

  const handleAddTag = async (e: Event) => {
    e.preventDefault();
    const dbInstance = drizzle();
    const name = newTagName().trim();
    if (!dbInstance || !name) {
      return;
    }
    const exists = (tagsQuery.data ?? []).some(
      (row) => (row.name ?? "").toLowerCase() === name.toLowerCase(),
    );
    if (exists) {
      showToast(t(msg`A tag with that name already exists`), "warning");
      return;
    }
    setIsAdding(true);
    try {
      const workspaceId = activeWorkspaceId() || (await ensureWorkspace(dbInstance, userId()));
      await dbInstance.insert(tag).values({
        id: generateId(),
        workspace_id: workspaceId,
        name,
        color: newTagColor(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      setNewTagName("");
      showToast(t(msg`Tag "${name}" created`), "success");
    } catch (err) {
      console.error("Failed to create tag", err);
      showToast(t(msg`Could not create the tag`), "error");
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteTag = async (row: Tag) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    const name = row.name ?? "";
    try {
      await patchRowVerified(dbInstance, tag, tag.id, row.id, {
        deleted_at: new Date().toISOString(),
      });
      showToast(t(msg`Tag "${name}" deleted`), "success");
    } catch (err) {
      console.error("Failed to delete tag", err);
      showToast(t(msg`Could not delete the tag`), "error");
    }
  };

  return (
    <GlassSurface class="p-6 bg-[#121212] border border-white/10 rounded-[24px] shadow-none max-w-2xl mx-auto">
      <div class="flex items-center gap-2 mb-1">
        <Tags class="h-4 w-4 text-[#afc6ff]" />
        <h3 class="text-sm font-bold text-foreground uppercase tracking-wider font-mono">
          {t(msg`Workspace Tags`)}
        </h3>
      </div>
      <p class="text-xs text-muted-foreground mb-4">
        {t(msg`Classify your time entries with tags shared by the team.`)}
      </p>

      <form onSubmit={handleAddTag} class="flex items-center gap-2 mb-4">
        <input
          type="text"
          placeholder={t(msg`New tag...`)}
          value={newTagName()}
          onInput={(e) => setNewTagName(e.currentTarget.value)}
          class="flex-1 bg-black/40 border border-white/10 rounded-[16px] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#afc6ff]/40 text-foreground placeholder-muted-foreground/60 font-mono"
        />
        <div class="flex items-center gap-1.5">
          <For each={TAG_COLOR_OPTIONS}>
            {(opt) => (
              <button
                type="button"
                title={opt.label}
                onClick={() => setNewTagColor(opt.value)}
                class={cn(
                  "w-5 h-5 rounded-full border-2 transition-all cursor-pointer",
                  newTagColor() === opt.value
                    ? "border-white scale-110"
                    : "border-transparent opacity-60 hover:opacity-100",
                )}
                style={{ "background-color": opt.value }}
              />
            )}
          </For>
        </div>
        <Button
          type="submit"
          disabled={isAdding() || !newTagName().trim() || !drizzle()}
          class="px-4 py-2.5 bg-primary text-black rounded-full font-bold font-mono text-xs cursor-pointer hover:bg-primary/95 shrink-0"
        >
          <Plus class="h-4 w-4" />
        </Button>
      </form>

      <Show
        when={(tagsQuery.data ?? []).length > 0}
        fallback={
          <p class="text-xs text-muted-foreground font-mono text-center py-4">
            {t(msg`No tags yet.`)}
          </p>
        }
      >
        <div class="flex flex-wrap gap-2">
          <For each={tagsQuery.data}>
            {(row) => (
              <span
                class="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full border text-xs font-mono group"
                style={{
                  "border-color": `${row.color ?? "#afc6ff"}40`,
                  color: row.color ?? "#afc6ff",
                  "background-color": `${row.color ?? "#afc6ff"}14`,
                }}
              >
                {row.name}
                <button
                  type="button"
                  aria-label={t(msg`Delete tag {name}`, { name: tagName(row) })}
                  onClick={() => void handleDeleteTag(row)}
                  class="opacity-40 group-hover:opacity-100 hover:bg-white/10 rounded-full p-0.5 transition-all cursor-pointer"
                >
                  <Trash2 class="h-3 w-3" />
                </button>
              </span>
            )}
          </For>
        </div>
      </Show>
    </GlassSurface>
  );
}
